import React, { useState, useEffect } from 'react'
import { getApiUrl, apiRequest } from '../utils/api';

const Analytics = () => {
  const [activeTab, setActiveTab] = useState('top-influencers');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortBy, setSortBy] = useState('total_views');
  const [limit, setLimit] = useState(10);
  
  // Data states
  const [topInfluencers, setTopInfluencers] = useState([]);
  const [topCampaigns, setTopCampaigns] = useState([]);
  const [roiAnalysis, setRoiAnalysis] = useState(null);
  
  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchTopInfluencers = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        sort_by: sortBy,
        limit: limit.toString()
      });
      
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      const response = await apiRequest(`/analytics/top-influencers?${params}`);
      const data = await response.json();
      
      if (data.success) {
        setTopInfluencers(data.top_influencers);
      } else {
        setError(data.error || 'Failed to fetch top influencers');
      }
    } catch (error) {
      console.error('Error fetching top influencers:', error);
      setError('Failed to fetch top influencers');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTopCampaigns = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        sort_by: sortBy,
        limit: limit.toString()
      });
      
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      const response = await apiRequest(`/analytics/top-campaigns?${params}`);
      const data = await response.json();
      
      if (data.success) {
        setTopCampaigns(data.top_campaigns);
      } else {
        setError(data.error || 'Failed to fetch top campaigns');
      }
    } catch (error) {
      console.error('Error fetching top campaigns:', error);
      setError('Failed to fetch top campaigns');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRoiAnalysis = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      const response = await apiRequest(`/analytics/roi-analysis?${params}`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (data.success && data.roi_analysis) {
        setRoiAnalysis(data.roi_analysis);
      } else {
        // Set a safe default structure if data is missing
        setRoiAnalysis({
          summary: {},
          roi_metrics: {},
          performance_distribution: {},
          cost_efficiency: {}
        });
        setError(data.error || 'ROI analysis data structure is incomplete');
      }
    } catch (error) {
      console.error('Error fetching ROI analysis:', error);
      setError('Failed to fetch ROI analysis. Please try again.');
      // Set a safe default structure on error
      setRoiAnalysis({
        summary: {},
        roi_metrics: {},
        performance_distribution: {},
        cost_efficiency: {}
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'top-influencers') {
      fetchTopInfluencers();
    } else if (activeTab === 'top-campaigns') {
      fetchTopCampaigns();
    } else if (activeTab === 'roi-analysis') {
      fetchRoiAnalysis();
    }
  }, [activeTab, startDate, endDate, sortBy, limit]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-IN').format(num);
  };

  const formatPercentage = (num) => {
    return (num * 100).toFixed(2) + '%';
  };

  const getSortOptions = () => {
    if (activeTab === 'top-influencers') {
      return [
        { value: 'total_views', label: 'Total Views' },
        { value: 'er_percentage', label: 'Avg Engagement Rate' },
        { value: 'tcs_score', label: 'Avg TCS Score' },
        { value: 'total_engagements', label: 'Total Engagements' },
        { value: 'total_spend', label: 'Total Spend' },
        { value: 'avg_cpe', label: 'Avg Cost Per Engagement' }
      ];
    } else if (activeTab === 'top-campaigns') {
      return [
        { value: 'er_percentage', label: 'Engagement Rate' },
        { value: 'tcs_score', label: 'TCS Score' },
        { value: 'views', label: 'Views' },
        { value: 'engagements', label: 'Engagements' },
        { value: 'cpv', label: 'Cost Per View' },
        { value: 'cpe', label: 'Cost Per Engagement' },
        { value: 'amount', label: 'Amount' }
      ];
    }
    return [];
  };

  const renderTopInfluencers = () => (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{topInfluencers.length}</div>
            <div className="text-sm text-gray-500">Top Influencers</div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {formatNumber(topInfluencers.reduce((sum, inf) => sum + inf.total_views, 0))}
            </div>
            <div className="text-sm text-gray-500">Total Views</div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">
              {formatNumber(topInfluencers.reduce((sum, inf) => sum + inf.total_engagements, 0))}
            </div>
            <div className="text-sm text-gray-500">Total Engagements</div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">
              {formatCurrency(topInfluencers.reduce((sum, inf) => sum + inf.total_spend, 0))}
            </div>
            <div className="text-sm text-gray-500">Total Spend</div>
          </div>
        </div>
      </div>

      {/* Influencers Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Top Influencers</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Influencer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaigns</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total Views</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Engagements</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Avg ER%</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Avg TCS</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total Spend</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Platforms</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {topInfluencers.map((influencer, index) => (
                <tr key={influencer.influencer_name} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-sm font-medium text-blue-600">#{index + 1}</span>
                      </div>
                      <div className="ml-3">
                        <div className="text-sm font-medium text-gray-900">{influencer.influencer_name}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{influencer.total_campaigns}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatNumber(influencer.total_views)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatNumber(influencer.total_engagements)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatPercentage(influencer.avg_er_percentage)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{Math.round(influencer.avg_tcs_score)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatCurrency(influencer.total_spend)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                    <div className="flex flex-wrap gap-1">
                      {influencer.platforms.map((platform, idx) => (
                        <span key={idx} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {platform}
                        </span>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderTopCampaigns = () => (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{topCampaigns.length}</div>
            <div className="text-sm text-gray-500">Top Campaigns</div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {formatNumber(topCampaigns.reduce((sum, camp) => sum + camp.views, 0))}
            </div>
            <div className="text-sm text-gray-500">Total Views</div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">
              {formatNumber(topCampaigns.reduce((sum, camp) => sum + camp.total_engagements, 0))}
            </div>
            <div className="text-sm text-gray-500">Total Engagements</div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">
              {formatCurrency(topCampaigns.reduce((sum, camp) => sum + camp.amount, 0))}
            </div>
            <div className="text-sm text-gray-500">Total Spend</div>
          </div>
        </div>
      </div>

      {/* Campaigns Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Top Campaigns</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Influencer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Platform</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Views</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Engagements</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">ER%</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">TCS</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">CPV</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">CPE</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {topCampaigns.map((campaign, index) => (
                <tr key={campaign.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                        <span className="text-sm font-medium text-green-600">#{index + 1}</span>
                      </div>
                      <div className="ml-3">
                        <div className="text-sm font-medium text-gray-900">{campaign.influencer_name}</div>
                        <div className="text-sm text-gray-500">{campaign.date}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {campaign.platform}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatNumber(campaign.views)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatNumber(campaign.total_engagements)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatPercentage(campaign.er_percentage)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{Math.round(campaign.tcs_score)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatCurrency(campaign.amount)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatCurrency(campaign.cpv)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatCurrency(campaign.cpe)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderRoiAnalysis = () => {
    if (!roiAnalysis) {
      return (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded">
          <p>ROI analysis data is not available. Please try refreshing the page.</p>
        </div>
      );
    }

    // Check if required nested objects exist
    const summary = roiAnalysis.summary || {};
    const roiMetrics = roiAnalysis.roi_metrics || {};
    const performanceDistribution = roiAnalysis.performance_distribution || {};
    const costEfficiency = roiAnalysis.cost_efficiency || {};

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{summary.total_campaigns || 0}</div>
              <div className="text-sm text-gray-500">Total Campaigns</div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{formatCurrency(summary.total_spend || 0)}</div>
              <div className="text-sm text-gray-500">Total Spend</div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">{formatNumber(summary.total_views || 0)}</div>
              <div className="text-sm text-gray-500">Total Views</div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">{formatNumber(summary.total_engagements || 0)}</div>
              <div className="text-sm text-gray-500">Total Engagements</div>
            </div>
          </div>
        </div>

        {/* ROI Metrics */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">ROI Metrics</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{formatCurrency(roiMetrics.cost_per_view || 0)}</div>
              <div className="text-sm text-gray-500">Cost Per View</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{formatCurrency(roiMetrics.cost_per_engagement || 0)}</div>
              <div className="text-sm text-gray-500">Cost Per Engagement</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">{formatNumber(roiMetrics.views_per_rupee || 0)}</div>
              <div className="text-sm text-gray-500">Views Per Rupee</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">{formatNumber(roiMetrics.engagements_per_rupee || 0)}</div>
              <div className="text-sm text-gray-500">Engagements Per Rupee</div>
            </div>
          </div>
        </div>

        {/* Performance Distribution */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Performance Distribution</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">High Performers (ER% &gt; 5%)</span>
                <span className="text-sm font-medium text-green-600">{performanceDistribution.high_performers || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Medium Performers (2-5% ER)</span>
                <span className="text-sm font-medium text-yellow-600">{performanceDistribution.medium_performers || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Low Performers (ER% &lt; 2%)</span>
                <span className="text-sm font-medium text-red-600">{performanceDistribution.low_performers || 0}</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Cost Efficiency</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">High Efficiency (CPV &lt; ₹1)</span>
                <span className="text-sm font-medium text-green-600">{costEfficiency.high_efficiency || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Medium Efficiency (₹1-5 CPV)</span>
                <span className="text-sm font-medium text-yellow-600">{costEfficiency.medium_efficiency || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Low Efficiency (CPV ≥ ₹5)</span>
                <span className="text-sm font-medium text-red-600">{costEfficiency.low_efficiency || 0}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Platform ROI */}
        {roiAnalysis.platform_roi && Object.keys(roiAnalysis.platform_roi).length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Platform ROI Analysis</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Platform</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Campaigns</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total Spend</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total Views</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">CPV</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">CPE</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {Object.entries(roiAnalysis.platform_roi).map(([platform, data]) => (
                    <tr key={platform}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{platform}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{data.campaign_count}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatCurrency(data.total_spend)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatNumber(data.total_views)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatCurrency(data.cost_per_view)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatCurrency(data.cost_per_engagement)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h2>
          <p className="text-gray-600">Comprehensive analysis of your influencer marketing campaigns</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              {getSortOptions().map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Limit</label>
            <select
              value={limit}
              onChange={(e) => setLimit(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value={5}>Top 5</option>
              <option value={10}>Top 10</option>
              <option value={20}>Top 20</option>
              <option value={50}>Top 50</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => {
                setStartDate('');
                setEndDate('');
              }}
              className="w-full px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('top-influencers')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'top-influencers'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              👥 Top Influencers
            </button>
            <button
              onClick={() => setActiveTab('top-campaigns')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'top-campaigns'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              🎯 Top Campaigns
            </button>
            <button
              onClick={() => setActiveTab('roi-analysis')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'roi-analysis'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              💰 ROI Analysis
            </button>
          </nav>
        </div>

        <div className="p-6">
          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-500">Loading analytics data...</p>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Data</h3>
              <p className="text-gray-500 mb-4">{error}</p>
            </div>
          ) : (
            <>
              {activeTab === 'top-influencers' && renderTopInfluencers()}
              {activeTab === 'top-campaigns' && renderTopCampaigns()}
              {activeTab === 'roi-analysis' && renderRoiAnalysis()}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Analytics;
